// src/helpers/api/server.ts

import type {
  UserServersResponse,
  SimplifiedDiscordRole,
  ServerConfig,
  ServerStats,
} from "@/helpers/interfaces/server";
import { apiFetch } from "../http-client";
import { handleBotKickedResponse } from "./botKickedHandler";
import { dedupedFetch, clearDedupedRequest } from "../requestDeduplicator";
import { getCachedData, invalidateCache } from "../apiCache";

// Standardized error wrapper to preserve HTTP status codes
const createApiError = (message: string, status: number, code?: string) => {
  const error: any = new Error(message);
  error.status = status;
  error.code = code;
  return error;
};

// 1. Fetch User's Servers
export const fetchUserServers = async (forceRefresh = false): Promise<UserServersResponse> => {
  const cacheKey = "user:servers";

  if (forceRefresh) {
    invalidateCache(cacheKey);
    clearDedupedRequest(cacheKey);
  }

  return dedupedFetch(cacheKey, () =>
    // Pass the forceRefresh flag down to the direct API call
    getCachedData(cacheKey, () => fetchUserServersDirectly(forceRefresh))
  );
};

async function fetchUserServersDirectly(forceRefresh = false): Promise<UserServersResponse> {
  // Append the force query parameter if requested
  const query = forceRefresh ? "?force=true" : "";
  const apiUrl = `${import.meta.env.VITE_API_URL}/api/user/servers${query}`;

  try {
    const response = await apiFetch(apiUrl, {
      method: "GET",
      credentials: "include",
      headers: { "Content-Type": "application/json", Accept: "application/json" },
    });

    if (!response.ok) {
      let errorMessage = `Failed to fetch servers: ${response.status} ${response.statusText}`;
      if (response.status === 401) {
        errorMessage = "Unauthorized. Please log in again.";
      } else {
        try {
          const errorData = await response.json();
          if (errorData?.error || errorData?.message) {
            errorMessage = errorData.error || errorData.message;
          }
        } catch (e) { /* ignore */ }
      }
      throw createApiError(errorMessage, response.status);
    }

    const data = await response.json();
    if (!data || !Array.isArray(data.guildsWithBot) || !Array.isArray(data.inviteableGuilds)) {
      throw new Error("Received invalid data format from the server.");
    }
    return data as UserServersResponse;
  } catch (err: any) {
    console.error("Error in fetchUserServers service:", err);
    throw err;
  }
}

// ... [Keep the rest of your server.ts exports identically exactly as they are] ...

// 2. Fetch Server Tier
export const fetchServerTierById = async (serverId: string, forceRefresh = false): Promise<boolean> => {
  if (!serverId || typeof serverId !== "string") {
    throw new Error("Invalid serverId provided for fetchServerTierById.");
  }

  const cacheKey = `server:tier:${serverId}`;

  if (forceRefresh) {
    invalidateCache(cacheKey);
    clearDedupedRequest(cacheKey);
  }

  return dedupedFetch(cacheKey, () =>
    getCachedData(cacheKey, () => fetchServerTierDirectly(serverId))
  );
};

async function fetchServerTierDirectly(serverId: string): Promise<boolean> {
  const apiUrl = `${import.meta.env.VITE_API_URL}/api/guilds/${serverId}/tier`;
  const response = await apiFetch(apiUrl, {
    method: "GET",
    credentials: "include",
    headers: { Accept: "application/json" },
  });

  let data: any;
  try {
    data = await response.json();
  } catch (parseError) {
    if (!response.ok) {
      throw createApiError(`Failed to parse response body`, response.status);
    }
    throw parseError;
  }

  handleBotKickedResponse(data, response.status);

  if (!response.ok) {
    throw createApiError(data?.error || data?.message || `HTTP ${response.status}`, response.status);
  }

  if (typeof data.isPremium !== "boolean") {
    throw new Error("Received invalid premium status data format.");
  }
  return data.isPremium;
}

export const refreshServerPremiumStatus = async (serverId: string): Promise<boolean> => {
  if (!serverId) return false;

  const { useContextStore } = await import("@/stores/context");
  const contextStore = useContextStore();

  try {
    contextStore.setServerTierStatus(serverId, "loading");
    const isPremium = await contextStore.getOrFetchServerTier(serverId, true);

    if (contextStore.userServers && Array.isArray(contextStore.userServers.guildsWithBot)) {
      const serverInList = contextStore.userServers.guildsWithBot.find(
        (s) => s.id === serverId
      ) as any;

      if (serverInList) {
        serverInList.isPremium = isPremium;
        serverInList.tierFetchStatus = "success";
      }
    }

    invalidateCache("user:servers");
    clearDedupedRequest("user:servers");
    invalidateCache(`server:tier:${serverId}`);
    clearDedupedRequest(`server:tier:${serverId}`);

    return isPremium;
  } catch (error) {
    contextStore.setServerTierStatus(serverId, "error");
    return false;
  }
};

// 3. Fetch Guild Roles
export const fetchGuildRoles = async (serverId: string): Promise<SimplifiedDiscordRole[]> => {
  const apiUrl = `${import.meta.env.VITE_API_URL}/api/guilds/${serverId}/roles`;
  try {
    const response = await apiFetch(apiUrl, { method: "GET", credentials: "include" });
    const data = await response.json();
    if (!response.ok) {
      handleBotKickedResponse(data, response.status);
      throw createApiError(data.error || data.message || "Failed to fetch roles", response.status);
    }
    return data as SimplifiedDiscordRole[];
  } catch (err: any) {
    throw err;
  }
};

// 4. Fetch Server Config
export const fetchServerConfig = async (serverId: string): Promise<ServerConfig> => {
  const apiUrl = `${import.meta.env.VITE_API_URL}/api/guilds/${serverId}/config`;
  try {
    const response = await apiFetch(apiUrl, { method: "GET", credentials: "include" });
    const data = await response.json();
    if (!response.ok) {
      handleBotKickedResponse(data, response.status);
      throw createApiError(data.error || data.message || "Failed to fetch server config", response.status);
    }
    return data as ServerConfig;
  } catch (err: any) {
    throw err;
  }
};

// 5. Update Server Config
export const updateServerConfig = async (
  serverId: string,
  configData: Partial<ServerConfig>
): Promise<ServerConfig> => {
  const apiUrl = `${import.meta.env.VITE_API_URL}/api/guilds/${serverId.trim()}/config`;
  try {
    const response = await apiFetch(apiUrl, {
      method: "PATCH",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(configData),
    });
    const data = await response.json();
    if (!response.ok) {
      handleBotKickedResponse(data, response.status);
      throw createApiError(data.error || data.message || "Failed to update server config", response.status);
    }
    return data as ServerConfig;
  } catch (err: any) {
    throw err;
  }
};

// 6. Fetch Server Stats
export const fetchServerStats = async (serverId: string): Promise<ServerStats> => {
  const apiUrl = `${import.meta.env.VITE_API_URL}/api/guilds/${serverId.trim()}/stats`;
  try {
    const response = await apiFetch(apiUrl, { method: "GET", credentials: "include" });
    let data: any;
    try {
      data = await response.json();
    } catch {
      throw createApiError(`Server returned non-JSON response`, response.status);
    }
    if (!response.ok) {
      handleBotKickedResponse(data, response.status);
      throw createApiError(data.error || data.message || "Failed to fetch stats", response.status, data.error);
    }
    return data as ServerStats;
  } catch (err: any) {
    throw err;
  }
};

// 7. Force Refresh User Guilds
export const forceRefreshUserServers = async (): Promise<void> => {
  const apiUrl = `${import.meta.env.VITE_API_URL}/api/user/servers/refresh`;
  const response = await apiFetch(apiUrl, { method: "POST", credentials: "include" });
  if (!response.ok) {
    const data = await response.json();
    throw createApiError(data.error || data.message || "Failed to refresh server list", response.status);
  }
};

// 8. Force Refresh Guild Data
export const forceRefreshGuildData = async (guildId: string): Promise<void> => {
  const apiUrl = `${import.meta.env.VITE_API_URL}/api/guilds/${guildId}/refresh`;
  const response = await apiFetch(apiUrl, { method: "POST", credentials: "include" });
  if (!response.ok) {
    const data = await response.json();
    throw createApiError(data.error || data.message || "Failed to refresh guild data", response.status);
  }
};

// 9. Reset Ticket Counter
export const resetTicketCounter = async (guildId: string, panelId: string, customCount: number = 0): Promise<void> => {
  const apiUrl = `${import.meta.env.VITE_API_URL}/api/guilds/${guildId}/panels/${panelId}/reset-counter`;
  const response = await apiFetch(apiUrl, {
    method: "POST",
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ newCount: customCount }),
  });
  if (!response.ok) {
    const data = await response.json();
    handleBotKickedResponse(data, response.status);
    throw createApiError(data.error || data.message || "Failed to update counter", response.status);
  }
};

// 10. Refresh Custom Bot Identity
export const refreshCustomBotIdentity = async (guildId: string): Promise<any> => {
  const apiUrl = `${import.meta.env.VITE_API_URL}/api/guilds/${guildId}/custom-bot/refresh`;
  const response = await apiFetch(apiUrl, { method: "POST", credentials: "include" });
  const data = await response.json();
  if (!response.ok) {
    handleBotKickedResponse(data, response.status);
    throw createApiError(data.error || data.message || "Failed to refresh identity", response.status);
  }
  return data.customBot;
};

// 11. Retry Custom Bot
export const retryCustomBotConnection = async (guildId: string): Promise<void> => {
  const apiUrl = `${import.meta.env.VITE_API_URL}/api/guilds/${guildId}/custom-bot/retry`;
  const response = await apiFetch(apiUrl, { method: "POST", credentials: "include" });
  if (!response.ok) {
    const data = await response.json();
    handleBotKickedResponse(data, response.status);
    throw createApiError(data.error || data.message || "Failed to retry connection", response.status);
  }
};

// 12. Get Google Auth URL
export const getGoogleAuthUrl = async (guildId: string): Promise<string> => {
  const apiUrl = `${import.meta.env.VITE_API_URL}/api/guilds/${guildId}/google/auth-url`;
  const response = await apiFetch(apiUrl, { method: "GET", credentials: "include" });
  const data = await response.json();
  if (!response.ok) {
    handleBotKickedResponse(data, response.status);
    throw createApiError(data.error || "Failed to get auth URL", response.status);
  }
  return data.url;
};

// 13. Disconnect Google Drive
export const disconnectGoogleDrive = async (guildId: string): Promise<void> => {
  const apiUrl = `${import.meta.env.VITE_API_URL}/api/guilds/${guildId}/google/disconnect`;
  const response = await apiFetch(apiUrl, { method: "POST", credentials: "include" });
  if (!response.ok) {
    const data = await response.json();
    handleBotKickedResponse(data, response.status);
    throw createApiError(data.error || "Failed to disconnect", response.status);
  }
};